import { SearchQuery } from '~/features/advanced-search/types';
import { SavedSearch } from '~/features/search/types/search';
import { intlService } from '~/i18n/IntlService';
import { pilotV3 } from '~/services/serviceInstances';
import { PilotId } from '~/types/PilotId';

import { JsonApiErrorResponse, JsonApiGetResponse } from '../../ApiTypes';
import { testIsAxiosError } from '../../utils';
import { SavedSearchRequestError } from './SavedSearchRequestError';
import { SavedSearchAttributes, SavedSearchMeta } from './types';
import { mapSavedSearchResponseToSavedSearch } from './utils';

type Request = Pick<
  SavedSearch,
  | 'name'
  | 'filters'
  | 'userIds'
  | 'departmentIds'
  | 'resultTable'
  | 'visibility'
> & {
  searchV2Id?: PilotId;
  query: SearchQuery;
};

type Response = JsonApiGetResponse<
  'saved-search',
  SavedSearchAttributes,
  void,
  SavedSearchMeta
>;
export const postSavedSearch = async (
  savedSearch: Request,
): Promise<SavedSearch> => {
  try {
    const response: Response = await pilotV3.post('/saved-search/', {
      data: {
        type: 'saved-search',
        attributes: {
          name: savedSearch.name,
          query: savedSearch.query,
          filters: savedSearch.filters,
          search_visibility: savedSearch.visibility,
          user_ids: savedSearch.userIds,
          department_ids: savedSearch.departmentIds,
          search_v2_id: savedSearch.searchV2Id,
          result_table: savedSearch.resultTable.map((column) => ({
            column_index: column.columnIndex,
            filter_id: column.filterId,
          })),
        },
      },
    });

    return mapSavedSearchResponseToSavedSearch(response);
  } catch (error) {
    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const [{ title, code }] = error.response.data.errors;
      throw new SavedSearchRequestError(title, code);
    }
    throw new Error(
      intlService.getIntl().formatMessage({
        id: 'postSavedSearch.createError',
        defaultMessage: 'An error occurred while creating a saved search.',
        description: 'Error message when saved search creation fails',
      }),
    );
  }
};
